'use client';

import { Fragment, ReactElement, useState } from 'react';
import cn from 'classnames';
import { FontIcon, ImageContainer, Lock, MovieDetails, Separator, Skeleton, Text } from '@intigral/shared';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import { MovieDetailsProps } from '@intigral/shared/components/MovieDetails';
import styles from './program-without-poster.module.sass';
import { Modal } from '../Modal';

export interface IProgram {
    channelImage: string;
    channelTitle: string;
    text: string;
    duration: string;
    name: string;
    PlayVideoButton?: any;
    /** If true Play button displayed; otherwise Info button displayed */
    showPlay: boolean;
    showProgress: boolean;
    progress: number;
    poster: string;
    rating?: string;
    description?: string;
    /** Badge with text, e.g. `live`, `rewind` */
    badge?: ReactElement;
    addReminderButton?: ReactElement;
    isReminderSet?: boolean;
    isLocked?: boolean;
    onLockClick?: () => void;
    isLoading?: boolean;
    detailsButton?: ReactElement;
}

export interface IProgramWithoutPoster {
    program: IProgram;
    /** Depending on `type` layout is changed, e.g. showing channel logo if type is `live` */
    type?: 'live' | 'rewind';
    /** If `true` live program's progress is shown */
    showProgress?: boolean;
    /** Asset duration, genre, etc. */
    assetDetails?: MovieDetailsProps;
    noHeight?: boolean;
    /**
     * CTAs (e.g. Play/Details buttons) to be shown in a drawer that user can open on mobile screens
     */
    drawerCTAs?: ReactElement[];
}

const SeparatorStyle = withResponsiveStyles(Separator);

const ProgramWithoutPoster = ({
    program,
    type = 'live',
    showProgress,
    noHeight,
    assetDetails,
    drawerCTAs = [],
}: IProgramWithoutPoster) => {
    const {
        channelImage,
        channelTitle,
        text,
        duration,
        poster,
        progress,
        addReminderButton,
        isReminderSet,
        isLocked = false,
        onLockClick = undefined,
        isLoading,
    } = program;
    const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);

    if (isLoading) {
        return (
            <div className={cn(styles.program, styles.skeleton)}>
                <Skeleton absolute />
            </div>
        );
    }

    return (
        <div className={cn(styles.program, noHeight && styles.noheight)}>
            <div className={cn(styles.details, { [styles['with-poster']]: poster })}>
                {showProgress && (
                    <div className={styles['progress-bar']}>
                        <div className={styles['progress-bar-done']} style={{ width: `${progress || '0'}%` }} />
                    </div>
                )}
                {channelImage && type === 'live' && (
                    <>
                        <div className={styles['channel-logo']}>
                            <ImageContainer src={channelImage} alt={channelTitle} />
                        </div>
                        <SeparatorStyle orientation="vertical" marginStart="1x" marginEnd="2x" />
                    </>
                )}
                <div className={styles.name}>
                    <Text
                        as="h3"
                        variant="text4"
                        alignment="start"
                        margin="0"
                        color="white"
                        fontWeight="regular"
                        title={text}
                    >
                        {text}
                    </Text>
                    {duration && (
                        <Text as="span" variant="text5" color="gray" className={styles.duration}>
                            {duration}
                        </Text>
                    )}
                    {assetDetails && (
                        <div className={styles['asset-details']}>
                            <MovieDetails {...assetDetails} />
                        </div>
                    )}
                </div>
                {isLocked ? (
                    // TODO: span instead div?
                    <div onClick={onLockClick}>
                        <Lock />
                    </div>
                ) : addReminderButton ? (
                    <span
                        className={cn(styles['reminder-cta'], { [styles.reminded]: isReminderSet })}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        {addReminderButton}
                    </span>
                ) : drawerCTAs.length ? (
                    // TODO: move to a separate component
                    <span
                        className={styles['drawer-trigger']}
                        onClick={(e) => {
                            e.preventDefault();
                            setIsDetailsPopupOpen(true);
                        }}
                    >
                        <FontIcon name="submenu-vertical" />
                    </span>
                ) : null}
            </div>

            <Modal
                open={isDetailsPopupOpen}
                onOpenChange={(open) => setIsDetailsPopupOpen(!!open)}
                alignSelf="bottom"
                closePlacement={'null'}
            >
                <Text as="h2" variant="h2">
                    {program.name || program.text}
                </Text>
                <div className="molded-buttons">
                    {drawerCTAs.map((cta, idx) => (
                        <Fragment key={`drawer-cta-${idx}`}>{cta}</Fragment>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export { ProgramWithoutPoster };
